import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },
    selectedVariantId: { type: Number },
    options: { type: Object },
    variantMetadata: { type: Object },
    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 440px;
      }

      .skio-plan-picker {
        display: flex;
        padding: 0;
        font-size: 15px;
        color: black;
        width: 100%;
        font-family: inherit;
        border: none;
        border-radius: var(--skio-group-border-radius, 0);
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: none;
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container.single {
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc)
      }

      .group-container input[type='radio']:checked + .group-label .skio-radio__container .skio-radio {
        opacity: 1!important;
        transform: scale(1)!important;
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }
      
      .group-container--selected .skio-radio {
        fill: var(--skio-group-border-selected-color, #333);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: .25rem .75rem .5rem .5rem;
        cursor: pointer;
      }

      @media (max-width: 420px) {
        .group-label {
          padding: .25rem .5rem .5rem .25rem;
        }
      }

      .one-time-below .group-container {
        margin: 1rem 0 0;
      }

      .one-time-below .group-container .group-label {
        border: 1px solid #1E1E1E;
        border-radius: 3px
      }

      .group-topline {
        display: flex;
        align-items: flex-start;
        width: 100%;
        gap: 8px;
      }

      .group-container:not(:first-of-type) .group-topline {
        margin-bottom: 1rem;
      }

      .group-container:not(:first-of-type) {
        margin-bottom: 1rem;
      }

      .group-container--selected .group-topline {
        font-weight: 700;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
        padding-top: 6px;
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

   

      .skio-price {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        align-items: flex-end;
        flex-wrap: wrap;
        text-align: right;
        gap: 4px;
        line-height: 1;
        margin-left: auto;
        vertical-align: middle;
        font-weight: 500;
        font-size: 18px;
        flex-shrink: 0;
        padding-top: 6px;
      }

      .skio-price-group {
        display: flex;
        gap: .25rem;
        align-items: center;
      }

      .skio-price s {
        font-size: 12px;
        color: #000;
        font-weight: 400;
        opacity: 0.75;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition: max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 0;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
      }

      /* Hide frequency if not selected */
      // .group-container:not(.group-container--selected) .group-content {
      //   pointer-events: none;
      //   opacity: 0;
      //   max-height: 0;
      //   visibility: hidden;
      // }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 70%;
        line-height: 1.5;
        font-weight: 400;
        font-size: 18px;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 0px 8px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        border-radius: 4px;
        font-size: 12px;
        color: var(--skio-discount-text-color, #fff);

        white-space: nowrap;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        outline: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin-top: 5px;
        padding: 4px 30px 4px 0;
        background-color: transparent;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0 top 50%;
        background-size: 16px;
        border: none;
        border-bottom: 2px solid #ccc;
        border-radius: 2px;
        font-size: 14px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selling-plan-dropdown-label {
        color: #000;
        font-weight: 500;
        line-height: 1;
        font-size: 12px;
        letter-spacing: normal;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-content-additional {
        margin-top: 0.75em;
        padding: 0 1.75rem;
      }

      .variant-title {
        text-align: center; 
        font-weight: 500; 
        font-size: .95rem; 
        letter-spacing: normal; 
        padding: 3px 0; 
        margin-bottom: 0; 
      }

      .skio-variants {
        padding: .75rem;
        border: 1px solid #1E1E1E;
        border-radius: 3px;
      }

      .skio-variants .variant-wrapper {
        display: inline-block; 
        vertical-align: top; 
        width: 100%; 
        border: 1px solid #1E1E1E; 
        border-radius: 3px; 
        box-shadow: 0 1px 2px rgba(0,0,0,0.03); 
        cursor: pointer;
      }

      .skio-variants .variant-wrapper:not(:last-of-type) {
        margin-bottom: 1rem;
      }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;

        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;

        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;

        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;

        margin-top: 0;
        margin-bottom: 0;

        letter-spacing: 0;
        line-height: 1.5;

        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;

        width: 2.25em;
        height: 2.25em;

        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;

        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;

        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }
      
      /* Custom */
      .group-content-additional ul {
        margin: 0;
        padding: 0;
        list-style: none;
        columns: 1;
        column-gap: 1rem;
      }
      @media (max-width: 768px) {
        .group-content-additional ul {
          columns: 2;
        }
      }
      @media (max-width: 420px) {
        .group-content-additional ul {
          columns: 1;
        }
      }
      .group-content-additional ul li {
        padding-left: 20px;
        background: url("data:image/svg+xml,%3Csvg width='17px' height='12px' viewBox='0 0 17 12' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Group-3'%3E%3Cg id='Clip-2'%3E%3C/g%3E%3Cpath d='M6.14554474,11.5311 C5.92141316,11.5311 5.70130789,11.4398368 5.54249211,11.2814684 L0.249228947,5.98731053 C-0.0831657895,5.65491579 -0.0831657895,5.11404737 0.249228947,4.78165263 C0.410281579,4.6206 0.624571053,4.53202105 0.852281579,4.53202105 C1.07999211,4.53202105 1.29383421,4.6206 1.45488684,4.78165263 L6.14554474,9.47275789 L15.3684921,0.249810526 C15.5295447,0.0887578947 15.7433868,0.000178947368 15.9710974,0.000178947368 C16.1988079,0.000178947368 16.4130974,0.0887578947 16.57415,0.249810526 C16.7352026,0.410863158 16.8237816,0.625152632 16.8237816,0.852863158 C16.8237816,1.08012632 16.7352026,1.29441579 16.57415,1.45546842 L6.74859737,11.2814684 C6.58978158,11.4398368 6.36967632,11.5311 6.14554474,11.5311' id='Fill-1' fill='%23657B73'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/svg%3E") left center no-repeat;
        background-size: 1em;
        margin-bottom: 0.25em;
        background-position: -2px 4px;
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables
    this.onetimeOptions = []
    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
    this.selectedVariantId = null;
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')
    
    this.product.variants = this.product.variants.map( variant => {
      const variantObj = {...variant}
      const variantMeta = this.variantMetadata ? this.variantMetadata[variant.id] : null;
      if ( variantMeta ) {
        variantObj.metadata = variantMeta
      }
      return variantObj
    })
    
    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  firstUpdated() {
    if (this.product?.variants?.length && !this.selectedVariantId) {
      let variantId = null
      const variantToggle = document.querySelector('.select-variant.selected')
      if (variantToggle) {
        variantId = variantToggle.getAttribute('data-variant-id')
      } else {
        const variantSelector = document.querySelector(`.product-info[data-product-id="${this.product.id}"] .select-container`);
        if ( variantSelector ) {
          const variantSelected = variantSelector.querySelector('.select-item.selected')
          variantId = variantSelected ? variantSelected.getAttribute('data-variant-id') : null
        } else {
          variantId = document.querySelector(`#product__options-${this.product.id}`).value
        }
      }
      this.selectedVariantId = variantId ? parseInt(variantId) : this.product.variants[0].id;
    }
  }

  handleVariantSelect(variantId) {
    this.selectedVariantId = variantId;
    this.selectedVariant = this.product.variants.find(v => v.id === variantId);
    this.variantChanged = true;
    // Dispatch custom event for variant update
    this.dispatchEvent(new CustomEvent('skio::update-variant', {
      bubbles: true,
      composed: true,
      detail: {
        variant: this.selectedVariant,
        key: this.key,
        sellingPlan: this.selectedSellingPlan,
      },
    }));
    // Update hidden select and dispatch change
    const select = this.renderRoot.querySelector('#skio-variant-select');
    if (select) {
      select.value = this.selectedVariant.id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#5F7898" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="#5F7898"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group, variant = null) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
          this.selectSellingPlan(e.target.value, variant)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${e => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}"> ${selling_plan.name} </label>
              `
            )
          : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group, variant = null) {

    if ( variant && variant.selling_plan_allocations?.length < 2 ) {
      return html`
        ${this.additionalContentText(variant)}
      `
    }
    
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector && this.options?.dropdownPosition == 'inside' ? 'margin-left' : ''}">
        ${this.options?.selector_type == 'dropdown' ? this.sellingPlanDropdown(group, variant) : ''}

        <!-- TODO: Make the label have the border instead of the group -->
        ${this.options?.selector_type == 'button' ? this.sellingPlanButtons(group) : ''} ${this.product.options.length > 1 ? '' : this.additionalContentText(variant)}
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it's not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (!this.product?.variants || this.product.variants.length === 0) {
      return html`<div>No variants found.</div>`;
    }

    const selectedVariant = this.product.variants.find( (variant, v) => {
      let variantId = null
      const variantToggle = document.querySelector('.select-variant.selected')
      if (variantToggle) {
        variantId = variantToggle.getAttribute('data-variant-id')
      } else {
        const variantSelector = document.querySelector(`.product-info[data-product-id="${this.product.id}"] .select-container`);
        if ( variantSelector ) {
          const variantSelected = variantSelector.querySelector('.select-item.selected')
          variantId = variantSelected ? variantSelected.getAttribute('data-variant-id') : null
        } else {
          variantId = document.querySelector(`#product__options-${this.product.id}`).value
        }
      }
      
      if ( variantId ) {
        if ( parseInt(variantId) === variant.id ) return variant
      } else {
        if ( this.selectedVariantId === variant.id ) return variant
        if ( !this.selectedVariantId && v === 0 ) return variant
      }
    })

    if ( selectedVariant ) {
      this.updateVariantLabels()
    }

    if ( this.product?.options?.length < 2 ) {
      return html`${this.renderPlanPickerForVariant(selectedVariant, this.selectedVariantId === selectedVariant.id, true)}`
    }

    const skioVariants = this.product.variants.map( variant => {
      if ( this.product.options.length > 1 && selectedVariant.option1 !== variant.option1 ) {
        return html``
      }
      const variantMeta = variant?.metadata
      const superlative = variantMeta?.superlative;
      const superlativeColor = variantMeta?.superlative_color;
      const superlativeBackground = variantMeta?.superlative_bg;

      return html`
        <div class="variant-wrapper">
          ${ superlative 
            ? html`<div class="variant-title" style="background: ${ superlativeBackground || '#aab7c7'}; color: ${ superlativeColor || '#222'};">${superlative}</div>`
            : ''
          }
          
          ${this.renderPlanPickerForVariant(variant, selectedVariant.id === variant.id)}
        </div>
      `
    })

    return html`
      <div class="skio-variants">
        ${skioVariants}
      </div>
      ${this.product.options.length > 1
        ? html`
          ${this.product.variants.map( variant => {

            if ( selectedVariant.option1 !== variant.option1 || variant?.metadata?.onetime_disabled ) return ''
            const selected = selectedVariant?.id === variant.id
            return html`<div class="one-time-below">${this.getOnetimeOption(variant, selected, false, true)}</div>`
          })}`
        : ''
      }
    `;
  }

  getOnetimeOption(variant, selected, single, showVariantTitle = false) {
    return !this.product.requires_selling_plan
      ? html`
          <div
            class="group-container ${selected && !this.selectedSellingPlan ? 'group-container--selected' : ''} ${!this.options?.onetime_first ? 'group-container--last' : ''}${single ? ' single' : ''}"
            @click=${e => { e.stopPropagation(); this.selectSellingPlanGroup(null, variant); }}
          >
            <input
              id="one-time-${this.key}-${variant.id}"
              class="group-input variant-selection-radio"
              name="skio-group-${this.key}"
              type="radio"
              ?checked=${selected && !this.selectedSellingPlan}
              value="One time purchase"
              @change=${e => { e.stopPropagation(); this.selectSellingPlanGroup(null, variant); }}
            />
            <label class="group-label" for="one-time-${this.key}-${variant.id}">
              <div class="group-topline">
                ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}
                <div class="group-title">
                  <span>${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}</span>
                  ${showVariantTitle ? html`<span style="display: block; font-size: 14px;">${variant.option2}</span>` : ''}
                </div>
                <div class="skio-price" aria-live="polite">
                  <div class="skio-price-group">
                    ${this.options?.show_compare_price && variant?.compare_at_price > variant.price
                      ? html`<s aria-hidden="true">${this.money(variant.compare_at_price)}</s>`
                      : ''}
                    ${this.money(variant.price)}
                  </div>
                  ${variant?.metadata?.one_time_price_label ? html`<sub class="selling-plan-dropdown-label">${variant.metadata.one_time_price_label}</sub>` : ''}
                </div>
              </div>
            </label>
          </div>
        `
      : ''
  }

  renderPlanPickerForVariant(variant, selected, single = false) {
    // Set up the plan picker UI for a single variant, using the existing logic but with the given variant as selectedVariant
    // You may want to refactor the plan picker logic into this method for clarity
    // const originalSelectedVariant = this.selectedVariant;
    // this.selectedVariant = variant;

    const variantSellingPlan = variant.selling_plan_allocations.find( (variantAlloc, v) => {
      if ( this.selectedSellingPlan ) return variantAlloc.selling_plan_id === this.selectedSellingPlan.id
      return v === 0
    })

    const onetimeOption = this.product.options.length <= 1 ? this.getOnetimeOption(variant, selected, single) : ''

    const result = html`
      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend-${variant.id}">
        <legend id="skio-plan-picker-legend-${variant.id}" class="${!this.options?.show_legend ? 'sr-only' : ''}">${unsafeHTML(this.options?.legend_content)}</legend>
        ${onetimeOption}
        ${this.availableSellingPlanGroups?.length
          ? this.availableSellingPlanGroups.map(
              (group, index) => {
                const variantSellingPlan = variant?.selling_plan_allocations.find( variantAlloc => variantAlloc.selling_plan_id === group.selected_selling_plan.id ) || variant.selling_plan_allocations[0]
                
                return html`
                  <div class="group-container ${selected && this.selectedSellingPlan ? 'group-container--selected' : ''}${single ? ' single' : ''}">
                    <input
                      id="group-${index}-${this.key}-${variant.id}"
                      class="group-input variant-selection-radio"
                      name="skio-group-${this.key}"
                      type="radio"
                      value="${group.id}"
                      data-something="hi"
                      ?checked=${selected && this.selectedSellingPlan}
                      @change=${e => { e.stopPropagation(); this.selectSellingPlanGroup(group, variant); }}
                    />
                    <label class="group-label" for="group-${index}-${this.key}-${variant.id}">
                      <div class="group-topline">
                        ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}
                        <div class="group-title">
                          ${
                            !variant?.metadata?.subscription_title 
                            ? group.name !== 'Prepaid' && this.options?.subscription_title
                              ? this.options?.subscription_title
                              : group.name == 'Prepaid' && this.options?.prepaid_title
                              ? this.options?.prepaid_title
                              : group.name
                            : variant.metadata.subscription_title
                          }
                              ${(this.discountText(group.selected_selling_plan) && !variant?.metadata?.subscription_title)
                                ? html` <span class="savings ${this.options?.discount_style}"> ${this.discountText(group.selected_selling_plan)} </span> `
                                : html``}
                            <sub class="selling-plan-subtitle" style="display: block;">${this.additionalFrequencyLabel(variant)}</sub>
                        </div>
                        <div class="skio-price" aria-live="polite">
                          <div class="skio-price-group">
                            ${this.options?.show_compare_price &&
                            (variant?.selling_plan_allocations?.length && variant.selling_plan_allocations[0].compare_at_price > variant.selling_plan_allocations[0].price )
                            // (variant?.compare_at_price > variant.price || variant.price > this.price(group.selected_selling_plan, false))
                              ? html`<s aria-hidden="true">${this.money(variant.compare_at_price ? variant.compare_at_price : variant.price)}</s>`
                              : ''}
                              ${this.price(variantSellingPlan, true, variant)}
                          </div>
                          ${variant?.metadata?.price_label ? html`<sub class="selling-plan-dropdown-label">${variant.metadata.price_label}</sub>` : ''}
                        </div>
                      </div>
                      ${selected && this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside' ? this.groupContent(group, variant) : ''}
                    </label>
                  </div>
                  
                  ${selected ? (this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath' ? this.groupContent(group, variant) : '') : ''}
                `
              }
            )
          : ''}
        ${this.options?.show_details == true ? this.showDetails() : ''}
      </fieldset>
    `;
    // this.selectedVariant = originalSelectedVariant;
    return result;
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  updateVariantLabels() {
    const variantLabel = document.querySelector('.product-info__servingSize')
    if ( variantLabel && this.selectedVariant?.title ) {
      variantLabel.innerHTML = this.selectedVariant.title
    }
  }

  bindFormEvents() {
    this.form = document.querySelector(`#${this.options?.form_id}`) || this.closest('form[action*="/cart/add"]')

    if (!this.form) {
      return
    }

    this.variantInput = this.form.querySelector('[name="id"]')

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
        this.updateVariantLabels()
      })
      // this.form.addEventListener('change', e => {
      //   this.selectedVariant = this.product.variants.find(variant => variant.id == this.variantInput.value)
      //   this.variantChanged = true
      // })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {

      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )
      

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // TODO: Select proper group depending on what selling plan is selected

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const variantToggle = document.querySelector('.select-variant.selected')
        let variantId = null
        if (variantToggle) {
          variantId = variantToggle.getAttribute('data-variant-id')
        } else {
          const variantSelector = document.querySelector(`.product-info[data-product-id="${this.product.id}"] .select-container`);
          if ( variantSelector ) {
            const variantSelected = variantSelector.querySelector('.select-item.selected')
            variantId = variantSelected ? variantSelected.getAttribute('data-variant-id') : null
          } else {
            variantId = document.querySelector(`#product__options-${this.product.id}`).value
          }
        }
        const selectedVariant = this.product.variants.find( variant => variant.id === parseInt(variantId) )

        const urlSelectedSellingPlanId = selectedVariant ? selectedVariant.selling_plan_allocations[0].selling_plan_id : url.searchParams.get('selling_plan')
    
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan

      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  //https://helloned.com/products/cbn-sleep-oil-full-spectrum-hemp?variant=40569725091925&selling_plan=6572671061&preview_theme_id=125174120533

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group, variant = null) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group ? group.selected_selling_plan : null;
    if (variant) {
      this.selectedVariantId = variant.id;
      this.selectedVariant = variant;
      this.variantChanged = true;
    }
    // Dispatch custom event for selling plan group selection
    this.dispatchEvent(new CustomEvent('skio::select-selling-plan-group', {
      bubbles: true,
      composed: true,
      detail: {
        variant: this.selectedVariant,
        group: group,
        key: this.key,
        sellingPlan: this.selectedSellingPlan,
        product: this.product,
      },
    }));
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id, variant = null) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)
    if ( variant ) {
      this.selectSellingPlanGroup(selectedGroup, variant)
    }
    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateVariantSelectors() {
    let variantSavings = null
    this.product.variants.forEach( variant => {
      const comparePriceElement = document.querySelector(`div[data-variant-id="${variant.id}"] .variant-option-label--compare`);
      const finalPriceElement = document.querySelector(`div[data-variant-id="${variant.id}"] .variant-option-label--final-price`);
      if ( comparePriceElement ) {
        if ( this.selectedSellingPlan ) {
          const currentSellingPlan = variant.selling_plan_allocations.find(selling_plan_allocation => selling_plan_allocation.selling_plan_id === this.selectedSellingPlan.id)
          if ( currentSellingPlan ) {
            if ( currentSellingPlan.per_delivery_price < variant.price ) {
              comparePriceElement.innerHTML = this.money(variant.price)
              comparePriceElement.style.display = 'inline-block'
            } else {
              comparePriceElement.innerHTML = ''
              comparePriceElement.style.display = 'none'
            }
          }
        } else {
          if ( variant.compare_at_price && variant.compare_at_price > variant.price ) {
            comparePriceElement.innerHTML = variant.compare_at_price
            comparePriceElement.style.display = 'inline-block'
          } else {
            comparePriceElement.innerHTML = ''
            comparePriceElement.style.display = 'none'
          }
        }
      }
      if ( finalPriceElement ) {
        if ( this.selectedSellingPlan ) {
          const currentSellingPlan = variant.selling_plan_allocations.find(selling_plan_allocation => selling_plan_allocation.selling_plan_id === this.selectedSellingPlan.id)
          if ( currentSellingPlan ) {
            finalPriceElement.innerHTML = this.money(currentSellingPlan.per_delivery_price)
          }
        } else {
          finalPriceElement.innerHTML = this.money(variant.price)
        }
      }

    })
    // if ( this.selectedSellingPlan ) {
    //   if ( sellingPlanDiscountType === 'percentage' ) {
    //     variantSavings = parseFloat(variantFullPrice) - (parseFloat(variantPrice) * ((100 - sellingPlanDiscount) / 100))
        
    //   } else {
    //     variantSavings = parseFloat(variantFullPrice) - sellingPlanDiscount
    //   }
    // } else {

    // }

    // const variantSelectors = this.form.querySelectorAll('.variant-selector')
    // // const variantSelectors = document.querySelectorAll('.product-variants-holder .product-variant ul .variant-selector')
    // if ( variantSelectors.length ) {
    //   const variantSavingsTagWrapper = variantSelector.querySelector('.variant-tag-save')
    //   const variantSavingsTag = variantSelector.querySelector('.variant-tag-save-amt')
    //   if ( variantSavingsTag ) {
    //     if ( variantSavings ) {
    //       variantSavings = variantSavings / 100
    //       // variantSavings = variantSavings % 1 === 0 ? variantSavings.toFixed(0) : variantSavings.toFixed(2);
    //       variantSavings = Math.round(variantSavings);
    //       variantSavingsTag.innerHTML = `$${variantSavings}`
    //       variantSavingsTagWrapper.style.display = 'flex'
    //     } else {
    //       variantSavingsTag.innerHTML = ''
    //       variantSavingsTagWrapper.style.display = 'none'
    //     }
    //   }
    // }
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })

    const variantOptions = this.form.querySelectorAll('.variant-option-label')
    
    if ( variantOptions.length > 1 ) {
      this.updateVariantSelectors()
    }
    
  }

  additionalFrequencyLabel(variant = null) {
    if ((!this.options?.additional_frequency_label || this.options?.additional_frequency_label == '') && !variant?.metadata?.subscription_subtitle) return

    return unsafeHTML(variant?.metadata?.subscription_subtitle || this.options?.additional_frequency_label)
  }

  renderNode(node) {
    switch (node.type) {
      case 'root':
        return node.children.map(child => this.renderNode(child)).join('');
      case 'list':
        const tag = node.listType === 'unordered' ? 'ul' : 'ol';
        return `<${tag}>${node.children.map(child => this.renderNode(child)).join('')}</${tag}>`;
      case 'list-item':
        return `<li>${node.children.map(child => this.renderNode(child)).join('')}</li>`;
      case 'paragraph':
        return `<p>${node.children.map(child => this.renderNode(child)).join('')}</p>`;
      case 'text':
        if ( node.bold ) {
          return `<span style="font-weight:500;">${node.value}</span>`;
        }
        return node.value;
      default:
        return '';
    }
  }

  additionalContentText(variant = false) {
    

    if ((!this.options?.additional_subscription_content || this.options?.additional_subscription_content == '') && !variant?.metadata?.details) return
    const subscriptionDetails = variant?.metadata?.details 
    ? this.renderNode(variant.metadata.details)
    : this.options?.additional_subscription_content
    return html`
      <div class="group-content-additional">
        ${unsafeHTML(
          subscriptionDetails
            .replaceAll(
              '[discount]',
              this.options?.discount_format === 'absolute' ? this.money(this.discount(this.selectedSellingPlan).absolute) : this.discount(this.selectedSellingPlan).percent + '%'
            )
            .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(this.selectedSellingPlan) || '')
        )}
      </div>
    `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return '' // Return empty if any invalid condition is met
    } else {
      return unsafeHTML(
        this.options?.discount_text
          .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
          .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
      )
    }
  }

  discount(selling_plan, variant = null) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0], variant)
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment, variant = null) {
    const currentVariant = variant || this.selectedVariant
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan?.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = currentVariant.price
    const compareAtPrice = currentVariant.compare_at_price && currentVariant.compare_at_price > price ? currentVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }
    
      let numString = (number / 100.0).toFixed(precision)
    
      // Remove leading zeros for numbers < 1 (e.g., "0.50" -> ".50")
      if (numString.startsWith('0') && numString.length > 1 && numString[1] === '.') {
        numString = numString.substring(1)
      }
    
      const parts = numString.split('.')
      let dollars = parts[0].replace(/^0+/, '') || '0' // Ensure at least "0"
      const cents = parts[1] ? decimal + parts[1] : ''
    
      // 🔥 Remove ".00" when no decimal values exist
      if (cents === `${decimal}00`) {
        return dollars
      }
    
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  isVisible(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  setButtonPrice(formatted, price, variant = null) {
    const formId = this.form.getAttribute('data-initial-variant-id')

    const variantIds = this.product.variants.map( variant => variant.id.toString() )
    if ( !variantIds.includes(formId) ) return 
    let priceInt = formatted ? parseFloat(price.replace('$', 0)) * 100 : price
    let priceFormatted = formatted ? price : this.money(price)
    const $addToCartText = this.form.querySelectorAll('.addToCartText')
    const $addToCartBtns = this.form.querySelectorAll('.productForm__addToCart')
    const $comparePrices = this.form.querySelectorAll('.addToCartPrice--compare')
    const $addToCartPrices = this.form.querySelectorAll('.addToCartPrice--final-price')
    
    $addToCartText.forEach(element => {
      element.innerHTML = variant && !variant.available ? 'SOLD OUT' : 'Add to Cart'; // Set new HTML content
    });
    $addToCartBtns.forEach(element => {
      element.disabled = variant && !variant.available
    });
    $addToCartPrices.forEach(element => {
      element.innerHTML = this.selectedSellingPlanGroup ? priceFormatted : this.money(this.selectedVariant.price); // Set new HTML content
      element.style.display = 'inline-block'
    });
    $comparePrices.forEach(element => {

      if ( ( this.selectedSellingPlanGroup && this.selectedVariant.price > priceInt ) || (!this.selectedSellingPlanGroup && parseFloat(this.selectedVariant.compare_at_price) > parseFloat(this.selectedVariant.price)) ) {
        element.innerHTML = this.selectedVariant.compare_at_price ? this.money(this.selectedVariant.compare_at_price) : this.money(this.selectedVariant.price)
        element.style.display = 'inline-block'
      } else {
        element.innerHTML = ''
        element.style.display = 'none'
      }
    });

  }

  // !SECTION: Discount Functions
  price(selling_plan, formatted = true, variant = null) {
    const currentVariant = variant || this.selectedVariant
    let formattedPrice = null
    if (this.rule || variant ) {
      const discountAmount = this.rule?.discountAmount || 0
      formattedPrice = formatted
        ? this.money(currentVariant.price - this.discount(selling_plan, currentVariant).amount - currentVariant.price * (discountAmount / 100))
        : currentVariant.price - this.discount(selling_plan, currentVariant).amount - currentVariant.price * (discountAmount / 100)
      if ( !discountAmount && selling_plan?.price_adjustments?.length ) {
        formattedPrice = this.money(selling_plan.price_adjustments[0].price)
      }
      if ( this.selectedVariantId === currentVariant?.id || !variant ) {
        this.setButtonPrice(formatted, formattedPrice, variant)
      }
      return formattedPrice
    }

    formattedPrice = formatted ? this.money(currentVariant.price - this.discount(selling_plan, currentVariant).amount) : currentVariant.price - this.discount(selling_plan, currentVariant).amount
    if ( variant && selling_plan?.price_adjustments?.length ) {
      formattedPrice = this.money(selling_plan.price_adjustments[0].price)
    }
    if ( this.selectedVariantId === currentVariant?.id || !currentVariant ) {
      this.setButtonPrice(formatted, formattedPrice, variant)
    }
    return formattedPrice
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker